import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AggregrationRerunService } from './aggregration-rerun.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  mergeEnvToBaseUrls,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-aggregration-rerun',
    templateUrl: './aggregration-rerun.component.html',
    styleUrls: ['./aggregration-rerun.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AggregrationRerunComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Rerun Aggregation for the selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following fir163jsync pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  syncSaveDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';
  aggLimit: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private aggregrationRerunService: AggregrationRerunService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = mergeEnvToBaseUrls(this.baseURLs, this.gtwGlobal.GENERAL_CONFIG) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gridPayload) && gridPayload.length > 0) {
      this.rowData = gridPayload;
    } else if (gridPayload && typeof gridPayload === 'object' && Object.keys(gridPayload).length > 0) {
      this.rowData = gridPayload;
    } else {
      this.rowData = rowPayload;
    }
    const asData: any = activeScreen?.data || {};
    const ms = asData?.modalScreen || (activeScreen as any)?.modalScreen;
    if (ms?.modal_name) {
      this.modalName = ms.modal_name;
    }
  }

  init(): void {
    // Following fir163jsync pattern - check if all required data is available
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    // Get logged in user from USER_SETTINGS like fir163jsync
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    // modalName is already set from header-data input (from ModalFactory.response.data.modalScreen)
    console.log('Modal Name:', this.modalName);

    // Update modal message based on modalName (matching AngularJS template conditions)
    // Reassign entire object so Angular detects the new reference for child component
    let message = 'Are you sure you want to Rerun Aggregation for the selected entities?';
    if (this.modalName === 'aggSecondaryAction') {
      message = 'Are you sure you want to initiate Secondary Action for the selected entities?';
    } else if (this.modalName === 'consolcalcSchJRerun') {
      message = 'Are you sure you want to Rerun Consol Calc Sch J for the selected entities?';
    } else if (this.modalName === 'consolcalcCreditsRerun') {
      message = 'Are you sure you want to Rerun Consol Calc Credits for the selected entities?';
    }
    this.modalConfig = { ...this.modalConfig, message };
    this.showModal = true;

    if (this.rowData) {
      this.processGridData();
    }

    this.fetchMaxCountEntities();
  }

  processGridData(): void {
    // Process grid data exactly like AngularJS controller
    if (this.rowData != null) {
      this.syncSaveDtls = [];
      
      // Clone the grid data like AngularJS
      const tempGridData = _.clone(this.rowData);
      const gridDataMultipleSelect = _.clone(tempGridData);
      
      if (gridDataMultipleSelect != undefined) {
        for (var key in gridDataMultipleSelect) {
          const gridObj: any = {};
          
          // Access data property like AngularJS: vm.gridDataMultipleSelect[key].data
          const dataItem = gridDataMultipleSelect[key].data || gridDataMultipleSelect[key];
          
          // Extract relevant fields based on AngularJS controller
          gridObj["object_id"] = dataItem.object_id;
          gridObj["combination_key"] = dataItem.COMBINATION_KEY;
          gridObj["consol_group_key"] = dataItem.CONSOL_GROUP_KEY;
          gridObj["leid"] = dataItem.LEID;
          gridObj["cdr_number"] = dataItem.CDR_NO;
          gridObj["me_code"] = dataItem.ME_CODE;
          gridObj["reporting_period"] = dataItem.REPORTING_PERIOD;
          gridObj["taxYear"] = dataItem.TAX_YEAR;
          gridObj["scenario"] = dataItem.SCENARIO;
          gridObj["startDate"] = this.formatDate(dataItem.TAX_YEAR_BEGIN);
          gridObj["EndDate"] = this.formatDate(dataItem.TAX_YEAR_END);
          gridObj["taxType"] = dataItem.TAX_TYPE;
          gridObj["ho_leid"] = dataItem.HO_LEID;
          gridObj["ho_cdr_number"] = dataItem.HO_CDR_NO;
          gridObj["ho_reporting_period"] = dataItem.HO_REPORTING_PERIOD;
          gridObj["ho_leName"] = dataItem.HO_LE_NAME;
          gridObj["parent_meCode"] = dataItem.PARENT_ME_CODE;

          this.syncSaveDtls.push(gridObj);
        }
      }
    }
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  fetchMaxCountEntities(): void {
    // Fetch aggregation limit like AngularJS controller
    this.aggregrationRerunService.fetchMaxCountEntities(
      this.GENERAL_CONFIG.base_url,
      this.globalService?.screen_key || '',
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result.jsonObject && result.jsonObject.length > 0) {
          this.aggLimit = result.jsonObject[0].ATTRIB_VALUE;
        }
      },
      (error: any) => {
        console.error('Error fetching aggregation limit:', error);
      }
    );
  }

  onConfirm(): void {
    if (this.modalName === 'consolcalcSchJRerun') {
      this.syncData('consolCalcSchJ');
    } else if (this.modalName === 'consolcalcCreditsRerun') {
      this.syncData('consolCalcCredits');
    } else {
      this.confirmSync();
    }
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-aggregration-rerun-ce')) {
      document.querySelector('gtw-aggregration-rerun-ce')?.dispatchEvent(updateEvent);
    }
  }
  
  onCancel(): void {
    // Called from confirmation modal - delegates to cancel()
    this.cancel();
  }

  confirmSync(): void {
    // Follow exact AngularJS behavior
    if (this.syncSaveDtls.length > parseInt(this.aggLimit)) {
      this.gtwSnackbarService.openSnackBar(`Can't select more than ${this.aggLimit} entities`, 'gtw-snackbar--red');
      return;
    }

    const returnClobSettingsObj: any = {};
    const ecSaveDtls: any[] = [];
    
    // Build settings object with real data (NOT hardcoded)
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['combination_key'] = this.buildCombinationKeys();
    
    let message = '';
    let type = '';
    
    if (this.modalName === 'aggSecondaryAction') {
      message = "Submitted for Aggregation Secondary Action";
      type = 'aggSecondaryAction';
      console.log('Type', type);
    } else {
      message = "Submitted for Aggregation Rerun";
      type = 'aggRerun';
      console.log('Type', type);
    }
    
    // Call sendDetails function like AngularJS
    this.sendDetails(returnClobSettingsObj, ecSaveDtls, message, type);
  }

  syncData(saveType: string): void {
    // Handle consol calc operations like AngularJS
    const returnClobSettingsObj: any = {};
    const ecSaveDtls: any[] = [];
    
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['combination_key'] = this.buildCombinationKeys();
    
    let message = '';
    if (saveType === 'consolCalcSchJ') {
      message = "Submitted for Consol Calc Sch J Reran";
    } else {
      message = "Submitted for Consol Calc Credits Reran";
    }
    
    this.sendConsolCalcDetails(returnClobSettingsObj, ecSaveDtls, message, saveType);
  }

  buildCombinationKeys(): string {
    // Build combination keys string exactly like AngularJS
    const ccKeys = this.syncSaveDtls.map(item => item.combination_key);
    return ccKeys.join(',');
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string, type: string): void {
    // Follow exact AngularJS sendDetails function behavior
    // Show loader (equivalent to $rootScope.$broadcast("showLoader", true))
    this.modalConfig.loading = true;
    
    // Dismiss modal immediately like AngularJS: $uibModalInstance.dismiss('cancel')
    this.cancel();
    
    // Hide loader (equivalent to $rootScope.$broadcast("showLoader", false))
    this.modalConfig.loading = false;
    
    // 🚀 CRITICAL: Route to correct service method based on type
    let serviceCall$;
    if (type === 'aggSecondaryAction') {
      // Secondary action uses dedicated service method (no clob_data/clob_settings/checkLockedbj)
      serviceCall$ = this.aggregrationRerunService.reRunAggSecondaryEntities(
        this.GENERAL_CONFIG.base_url,
        returnClobSettingsObj,
        ecSaveDtls,
        type,
        this.globalService,
        this.filterParams,
        this.rowData
      );
    } else {
      // Aggregation rerun uses the main service method
      serviceCall$ = this.aggregrationRerunService.reRunAggEntities(
        this.GENERAL_CONFIG.base_url,
        returnClobSettingsObj,
        ecSaveDtls,
        type,
        this.globalService,
        this.filterParams,
        this.rowData
      );
    }

    serviceCall$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        // Handle response exactly like AngularJS
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show success message AFTER API call succeeds (like AngularJS)
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        }
      },
      (error: any) => {
        console.error('Aggregation rerun error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while rerunning aggregation. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  sendConsolCalcDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string, saveType: string): void {
    // Follow exact AngularJS sendConsolCalcDetails function behavior
    // Show loader (equivalent to $rootScope.$broadcast("showLoader", true))
    this.modalConfig.loading = true;
    
    // Dismiss modal immediately like AngularJS: $uibModalInstance.dismiss('cancel')
    this.cancel();
    
    // Call service method like AngularJS
    this.aggregrationRerunService.reRunAggEntities(
      this.GENERAL_CONFIG.base_url, // Use base_url like fir163jsync
      returnClobSettingsObj, // Pass returnClobSettingsObj
      ecSaveDtls, // Pass ecSaveDtls array (empty array [])
      saveType, // Pass saveType
      this.globalService, // Pass globalService like fir163jsync
      this.filterParams, // Pass filterParams
      this.rowData // Pass rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        // Handle response exactly like AngularJS
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          // Hide loader (equivalent to $rootScope.$broadcast("showLoader", false))
          this.modalConfig.loading = false;
          
          // Show success message AFTER API call succeeds (like AngularJS)
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        }
      },
      (error: any) => {
        console.error('Consol calc error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while processing consol calc. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}
